<template>
    <div class="stats-bar" :class="{ 'stats-bar_bold': bold }" v-if="stats">
        <span class="stats-bar__label" v-if="title">{{ title }}</span>

        <button
            type="button"
            class="stats-bar__item stats-bar__item_all"
            :class="{ 'stats-bar__item_active': activeStatus.length === 0 }"
            @click.stop="onStatusClick(null)"
        >
            {{ allLabel }}
        </button>

        <button
            type="button"
            class="stats-bar__item"
            :class="{ 'stats-bar__item_active': isActive('new') }"
            style="--dot: #2f80ed"
            v-if="showNew && (stats.new > 0 || isActive('new'))"
            @click.stop="onStatusClick('new')"
        >
            <span class="stats-bar__dot"></span>
            Новые
            <span class="stats-bar__count" :title="stats.new">{{ formatCount(stats.new) }}</span>
        </button>

        <button
            type="button"
            class="stats-bar__item"
            :class="{ 'stats-bar__item_active': isActive(item.status) }"
            :style="{ '--dot': statusColor(item.status) }"
            v-for="item in displayStats"
            :key="item.status"
            @click.stop="onStatusClick(item.status)"
        >
            <span class="stats-bar__dot"></span>
            {{ item.label }}
            <span class="stats-bar__count" :title="item.count">{{ formatCount(item.count) }}</span>
        </button>

        <!-- Псевдостатусы: не значения колонки status, а отдельные срезы выборки.
             У заявок это «Просроченные», у оборудования — две просрочки сразу. -->
        <button
            type="button"
            class="stats-bar__item stats-bar__item_overdue"
            :class="{ 'stats-bar__item_active': isActive(extra.key) }"
            :style="{ '--dot': extra.color }"
            :key="extra.key"
            v-for="extra in displayExtraItems"
            @click.stop="onStatusClick(extra.key)"
        >
            <span class="stats-bar__dot"></span>
            {{ extra.label }}
            <span class="stats-bar__count" :title="extra.count">{{ formatCount(extra.count) }}</span>
        </button>
    </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import colors from '@/js/enums/RequestStatusColor';

const STATUS_LABELS = {
    0: 'Закрыто',
    1: 'Отказано',
    2: 'Приостановлено',
    3: 'На уточнении',
    4: 'В работе',
    5: 'Выполнено',
};

// Порядок пилюль у заявок: сначала то, что в работе, закрытое — в конце.
const STATUS_ORDER = [4, 3, 2, 0, 5, 1];

/**
 * Строка статусов со счётчиками — основной способ фильтрации списка.
 *
 * Компонент общий для заявок и оборудования. Всё, что зависит от домена, вынесено в props,
 * а их значения по умолчанию равны тому, что раньше было захардкожено под заявки: так
 * список обращений передаёт ровно то, что передавал, и его поведение не меняется.
 *
 * Псевдостатусы («Просроченные» у заявок, две просрочки у оборудования) — это не значения
 * колонки status, а отдельные срезы выборки, поэтому живут в extraItems, а не в labels.
 */
@Component()
class StatsBar extends Vue {
    @Prop({ type: Object, default: null }) stats;
    @Prop({ type: Array, default: () => ['0', '1', '2', '3', '4', '5'] }) visibleStatuses;
    @Prop({ type: Boolean, default: true }) showOverdue;
    @Prop({ type: Array, default: () => [] }) activeStatus;

    @Prop({ type: String, default: 'СТАТУС' }) title;
    @Prop({ type: String, default: 'Любой' }) allLabel;
    @Prop({ type: Object, default: () => STATUS_LABELS }) labels;
    @Prop({ type: Object, default: () => colors }) colors;
    @Prop({ type: Array, default: () => STATUS_ORDER }) order;

    // «Новые» есть только у заявок: это срез по дате, а не значение статуса.
    @Prop({ type: Boolean, default: true }) showNew;

    // Крупный вариант: чипы больше, цвет статуса уходит в рамку и счётчик, а активный
    // заливается своим цветом. В обычном варианте активный всегда зелёный — при активации
    // цвет статуса теряется, и на строке из шести статусов это мешает.
    // По умолчанию выключен: список обращений остаётся ровно таким, каким был.
    @Prop({ type: Boolean, default: false }) bold;

    // [{ key, label, color, countKey }] — срезы, не являющиеся статусами.
    @Prop({
        type: Array,
        default: () => [{ key: 'overdue', label: 'Просроченные', color: '#e53935', countKey: 'overdue' }],
    })
    extraItems;

    get displayStats() {
        if (!this.stats?.statuses) return [];

        return this.order
            .filter((status) => this.visibleStatuses.includes(String(status)))
            .map((status) => ({
                status,
                label: this.labels[status],
                count: this.stats.statuses[status] ?? 0,
            }))
            .filter((item) => item.count > 0 || this.isActive(item.status));
    }

    get displayExtraItems() {
        if (!this.stats || !this.showOverdue) return [];

        return this.extraItems
            .map((item) => ({ ...item, count: this.stats[item.countKey] ?? 0 }))
            .filter((item) => item.count > 0 || this.isActive(item.key));
    }

    isActive(status) {
        return this.activeStatus.includes(status);
    }

    onStatusClick(status) {
        this.$emit('filter-by-status', status);
    }

    statusColor(status) {
        return this.colors[status] || '#9aa0a6';
    }

    formatCount(count) {
        if (count >= 1000) {
            return `${Math.floor(count / 1000)}к`;
        }

        return String(count);
    }
}

export default StatsBar;
</script>

<style lang="scss">
.stats-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 6px 4px;
    margin-bottom: 10px;
    font-size: 0.82rem;

    &__label {
        font-size: 0.78rem;
        font-weight: 600;
        color: #9aa0a6;
        margin-right: 4px;
    }

    &__item {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        white-space: nowrap;
        cursor: pointer;
        border: 1px solid #e3e5e8;
        background: #fff;
        color: #3c4043;
        padding: 6px 14px;
        border-radius: 999px;
        font-size: 0.82rem;
        font-weight: 500;
        line-height: 1;
        transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;

        &:hover {
            background: #f6f7f8;
            border-color: #d4d7db;
        }
    }

    &__count {
        font-size: 0.72rem;
        font-weight: 600;
        color: #9aa0a6;
    }

    &__dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--dot, #9aa0a6);
        flex-shrink: 0;
    }

    &__item_active {
        background: #e8f5e9;
        border-color: #a5d6a7;
        color: #2e7d32;
        font-weight: 600;

        .stats-bar__count {
            color: #2e7d32;
        }

        &.stats-bar__item_overdue {
            background: #ffebee;
            border-color: #ef9a9a;
            color: #c62828;

            .stats-bar__count {
                color: #c62828;
            }
        }
    }

    // Крупный вариант. Цвет статуса приходит инлайн в --dot, поэтому и рамка, и счётчик,
    // и заливка активного берутся из него — одна переменная на чип, без таблицы классов.
    &_bold {
        gap: 10px;
        margin-bottom: 0;

        .stats-bar__item {
            padding: 9px 16px;
            font-size: 0.875rem;
            border-color: var(--dot, #e3e5e8);
            color: #3c4043;
        }

        .stats-bar__dot {
            width: 9px;
            height: 9px;
        }

        .stats-bar__count {
            font-size: 0.8125rem;
            font-weight: 700;
            color: var(--dot, #9aa0a6);
        }

        .stats-bar__item_active,
        .stats-bar__item_active.stats-bar__item_overdue {
            background: var(--dot, var(--color-green));
            border-color: var(--dot, var(--color-green));
            color: #fff;

            .stats-bar__count {
                color: #fff;
            }

            .stats-bar__dot {
                background: #fff;
            }
        }

        // У кнопки «Любой» своего цвета нет: она не статус, а снятие выбора.
        .stats-bar__item_all.stats-bar__item_active {
            background: #3c4043;
            border-color: #3c4043;
        }
    }
}
</style>
